import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Description as DocumentIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Label as LabelIcon,
  Link as LinkIcon,
  History as HistoryIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { ApiService } from '../../services/ApiService';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../types/user';
import { 
  DocumentVersionHistory, 
  DocumentVersionUpload, 
  DocumentVersionCompare 
} from '../../components/document/DocumentVersion';

interface Document {
  id: string;
  title: string;
  description: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  category: 'policy' | 'template' | 'evidence' | 'report' | 'other';
  uploadedBy: string;
  uploadedAt: string;
  requirementId?: string;
  requirementTitle?: string;
  tags: string[];
  version?: number;
  version_notes?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`document-tabpanel-${index}`}
      aria-labelledby={`document-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function MaterialDocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [document, setDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showVersionUpload, setShowVersionUpload] = useState(false);
  const [showVersionCompare, setShowVersionCompare] = useState(false);
  const [compareVersionIds, setCompareVersionIds] = useState<[number, number] | null>(null);
  
  const canEdit = hasPermission(Permission.DOCUMENT_EDIT);
  const canDelete = hasPermission(Permission.DOCUMENT_DELETE);

  useEffect(() => {
    if (id) {
      fetchDocument();
    }
  }, [id]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get<Document>(`/documents/${id}`);
      setDocument(response);
    } catch (error) {
      console.error('Error fetching document:', error);
      setError('Failed to load document details');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!document) return;
    
    try {
      setDownloading(true);
      // In a real app, you would call the download endpoint
      const downloadUrl = `/api/documents/${id}/download`;
      
      // Create a temporary anchor element to trigger download
      const link = window.document.createElement('a');
      link.href = downloadUrl;
      link.download = document.fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      
      console.log('Downloading document:', document.fileName);
    } catch (error) {
      console.error('Error downloading document:', error);
      setError('Failed to download document');
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await ApiService.delete(`/documents/${id}`);
      navigate('/documents');
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document');
    }
    setDeleteDialogOpen(false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleVersionUploaded = (newVersion: number) => {
    setShowVersionUpload(false);
    fetchDocument(); // Refresh document to get latest version info
  };

  const handleCompareVersions = (fromVersion: number, toVersion: number) => {
    setCompareVersionIds([fromVersion, toVersion]);
    setShowVersionCompare(true);
  };

  const getCategoryColor = (category: Document['category']) => {
    switch (category) {
      case 'policy':
        return 'primary';
      case 'template':
        return 'secondary';
      case 'evidence':
        return 'success';
      case 'report':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = () => {
    if (!document) return <DocumentIcon />;
    
    const extension = document.fileName.split('.').pop()?.toLowerCase();
    // You could return different icons based on file type
    switch (extension) {
      case 'pdf':
        return <DocumentIcon sx={{ color: '#d32f2f' }} />;
      case 'doc':
      case 'docx':
        return <DocumentIcon sx={{ color: '#1976d2' }} />;
      case 'xls':
      case 'xlsx':
        return <DocumentIcon sx={{ color: '#388e3c' }} />;
      default:
        return <DocumentIcon />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !document) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Document not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/documents')}
        >
          Back to Documents
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/documents')} title="Back to documents">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" data-testid="document-detail-title">
            {document?.title || 'Document Details'}
          </Typography>
          {document?.version && (
            <Chip 
              label={`v${document.version}`} 
              size="small" 
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleDownload}
            disabled={downloading}
            data-testid="download-button"
          >
            {downloading ? 'Downloading...' : 'Download'}
          </Button>
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/documents/${id}/edit`)}
              data-testid="edit-button"
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="delete-button"
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Details" />
          <Tab 
            label="Version History" 
            icon={<HistoryIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {/* Document Information */}
        <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ mr: 2, fontSize: 48 }}>
                  {getFileIcon()}
                </Box>
                <Box>
                  <Typography variant="h5" gutterBottom data-testid="document-title">
                    {document.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" data-testid="document-filename">
                    {document.fileName}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1" paragraph data-testid="document-description">
                {document.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {/* Tags */}
              {document.tags && document.tags.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <LabelIcon sx={{ mr: 1 }} /> Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {document.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        data-testid={`tag-${index}`}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Linked Requirement */}
              {document.requirementId && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <LinkIcon sx={{ mr: 1 }} /> Linked Requirement
                  </Typography>
                  <Button
                    component={Link}
                    to={`/requirements/${document.requirementId}`}
                    variant="text"
                    data-testid="linked-requirement"
                  >
                    {document.requirementTitle}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Metadata Sidebar */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Document Information
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CategoryIcon sx={{ mr: 1, fontSize: 20 }} /> Category
                  </Typography>
                  <Chip
                    label={document.category}
                    color={getCategoryColor(document.category)}
                    size="small"
                    sx={{ mt: 0.5 }}
                    data-testid="document-category"
                  />
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    File Size
                  </Typography>
                  <Typography variant="body1" data-testid="document-size">
                    {formatFileSize(document.fileSize)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    File Type
                  </Typography>
                  <Typography variant="body1" data-testid="document-type">
                    {document.fileType}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1, fontSize: 20 }} /> Uploaded By
                  </Typography>
                  <Typography variant="body1" data-testid="document-uploaded-by">
                    {document.uploadedBy}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarIcon sx={{ mr: 1, fontSize: 20 }} /> Uploaded On
                  </Typography>
                  <Typography variant="body1" data-testid="document-uploaded-date">
                    {new Date(document.uploadedAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(document.uploadedAt).toLocaleTimeString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </TabPanel>

      {/* Version History Tab */}
      <TabPanel value={activeTab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setShowVersionUpload(true)}
              data-testid="upload-version-button"
            >
              Upload New Version
            </Button>
          )}
        </Box>
        
        <DocumentVersionHistory 
          documentId={id!} 
          onVersionCompare={handleCompareVersions}
        />
      </TabPanel>

      {/* Version Upload Dialog */}
      {showVersionUpload && (
        <Dialog open={showVersionUpload} onClose={() => setShowVersionUpload(false)} maxWidth="md" fullWidth>
          <DialogContent>
            <DocumentVersionUpload
              documentId={id!}
              documentTitle={document?.title || ''}
              currentVersion={document?.version || 1}
              onUploadSuccess={handleVersionUploaded}
              onCancel={() => setShowVersionUpload(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Version Compare Dialog */}
      {compareVersionIds && (
        <Dialog open={showVersionCompare} onClose={() => {
          setShowVersionCompare(false);
          setCompareVersionIds(null);
        }} maxWidth="lg" fullWidth>
          <DialogContent>
            <DocumentVersionCompare
              documentId={id!}
              fromVersion={compareVersionIds[0]}
              toVersion={compareVersionIds[1]}
              onClose={() => {
                setShowVersionCompare(false);
                setCompareVersionIds(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        data-testid="delete-dialog"
      >
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{document.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
